import { useState, useEffect } from "react";
import { api, errMsg } from "../../lib/api";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

const empty = { code: "", percent_off: 10, active: true, expires_at: null, max_uses: null };

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState(null);
  const [banner, setBanner] = useState(null);
  const load = () => api.get("/admin/coupons").then((r) => setCoupons(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/promo-banner").then((r) => setBanner(r.data)).catch(() => {});
  }, []);

  const saveBanner = async (next) => {
    try {
      await api.put("/admin/promo-banner", { enabled: next.enabled, coupon_code: next.coupon_code || "", message: next.message || "" });
      setBanner(next);
      toast.success(next.enabled ? "Promo banner is LIVE ✓" : "Promo banner turned off");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const save = async (e) => {
    e.preventDefault();
    const body = { ...form, percent_off: parseInt(form.percent_off) || 0,
      max_uses: form.max_uses === "" || form.max_uses == null ? null : parseInt(form.max_uses),
      expires_at: form.expires_at || null };
    try {
      if (form.id) await api.put(`/admin/coupons/${form.id}`, body);
      else await api.post("/admin/coupons", body);
      toast.success("Coupon saved ✓");
      setForm(null); load();
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this coupon?")) return;
    await api.delete(`/admin/coupons/${id}`);
    toast.success("Coupon deleted"); load();
  };

  const toggle = async (c) => {
    await api.put(`/admin/coupons/${c.id}`, { code: c.code, percent_off: c.percent_off, active: !c.active, expires_at: c.expires_at, max_uses: c.max_uses });
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display font-bold text-xl" data-testid="admin-coupons-title">COUPON CODES</h1>
        <button data-testid="new-coupon-btn" onClick={() => setForm({ ...empty })} className="btn-primary !py-1.5 text-sm flex items-center gap-1"><Plus size={15} /> NEW COUPON</button>
      </div>
      {banner && (
        <div className="forge-card !transform-none p-4 mb-6" data-testid="promo-banner-settings">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="font-display font-bold text-sm" style={{ color: "var(--gold)", letterSpacing: 1 }}>SITE PROMO BANNER</p>
              <p className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>Show a featured coupon code at the top of the store for all visitors.</p>
            </div>
            <button data-testid="promo-banner-toggle"
              onClick={() => {
                if (!banner.enabled && !banner.coupon_code) { toast.error("Pick a coupon to feature first"); return; }
                saveBanner({ ...banner, enabled: !banner.enabled });
              }}
              className="text-xs font-display px-3 py-1.5 rounded border"
              style={banner.enabled ? { color: "var(--land)", borderColor: "rgba(82,210,115,.4)" } : { color: "var(--muted)", borderColor: "var(--line)" }}>
              {banner.enabled ? "● LIVE" : "OFF"}
            </button>
          </div>
          <div className="grid sm:grid-cols-[180px_1fr_auto] gap-2 items-end">
            <div>
              <label className="text-xs" style={{ color: "var(--muted)" }}>Featured coupon</label>
              <select data-testid="promo-banner-coupon-select" value={banner.coupon_code || ""}
                onChange={(e) => setBanner({ ...banner, coupon_code: e.target.value })}
                className="input-dark text-sm mt-1">
                <option value="">— select —</option>
                {coupons.filter((c) => c.active).map((c) => (
                  <option key={c.id} value={c.code}>{c.code} (−{c.percent_off}%)</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs" style={{ color: "var(--muted)" }}>Banner message (optional)</label>
              <input data-testid="promo-banner-message-input" value={banner.message || ""}
                onChange={(e) => setBanner({ ...banner, message: e.target.value })}
                placeholder="e.g. Summer sale — save on every course!" className="input-dark text-sm mt-1" />
            </div>
            <button data-testid="promo-banner-save" onClick={() => saveBanner(banner)} className="btn-primary !py-2 text-sm">SAVE</button>
          </div>
        </div>
      )}
      {form && (
        <form onSubmit={save} className="forge-card p-4 mb-5 grid sm:grid-cols-5 gap-2 items-end" data-testid="coupon-form">
          <div><label className="text-xs" style={{ color: "var(--muted)" }}>Code</label>
            <input data-testid="coupon-code-input" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="FORGE20" className="input-dark text-sm mt-1" /></div>
          <div><label className="text-xs" style={{ color: "var(--muted)" }}>% off (1-100)</label>
            <input data-testid="coupon-percent-input" required type="number" min="1" max="100" value={form.percent_off} onChange={(e) => setForm({ ...form, percent_off: e.target.value })} className="input-dark text-sm mt-1" /></div>
          <div><label className="text-xs" style={{ color: "var(--muted)" }}>Expires (optional)</label>
            <input type="datetime-local" value={form.expires_at ? form.expires_at.slice(0, 16) : ""} onChange={(e) => setForm({ ...form, expires_at: e.target.value ? new Date(e.target.value).toISOString() : null })} className="input-dark text-sm mt-1" /></div>
          <div><label className="text-xs" style={{ color: "var(--muted)" }}>Max uses (optional)</label>
            <input type="number" min="1" value={form.max_uses ?? ""} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder="∞" className="input-dark text-sm mt-1" /></div>
          <div className="flex gap-2">
            <button className="btn-primary !py-2 text-sm" data-testid="coupon-save-btn">SAVE</button>
            <button type="button" onClick={() => setForm(null)} className="btn-ghost !py-2 text-sm">✕</button>
          </div>
        </form>
      )}
      <div className="space-y-2">
        {coupons.length === 0 && <div className="empty" data-testid="no-coupons">No coupons yet — create one and share the code with students.</div>}
        {coupons.map((c) => (
          <div key={c.id} className="forge-card flex items-center gap-4 p-3 text-sm" data-testid={`coupon-${c.code}`}>
            <span className="font-display font-bold" style={{ color: "var(--gold)", letterSpacing: 1 }}>{c.code}</span>
            <span style={{ color: "var(--land)" }}>−{c.percent_off}%</span>
            <span className="text-xs" style={{ color: "var(--muted)" }}>
              {c.uses || 0}{c.max_uses ? `/${c.max_uses}` : ""} uses
              {c.expires_at && ` · expires ${new Date(c.expires_at).toLocaleDateString()}`}
            </span>
            <button data-testid={`toggle-coupon-${c.code}`} onClick={() => toggle(c)}
              className="ml-auto text-xs font-display px-2 py-0.5 rounded border"
              style={c.active ? { color: "var(--land)", borderColor: "rgba(82,210,115,.4)" } : { color: "var(--muted)", borderColor: "var(--line)" }}>
              {c.active ? "ACTIVE" : "OFF"}
            </button>
            <button data-testid={`edit-coupon-${c.code}`} onClick={() => setForm({ ...c })} className="text-xs" style={{ color: "var(--bp)" }}>Edit</button>
            <button data-testid={`delete-coupon-${c.code}`} onClick={() => remove(c.id)} className="p-1 hover:text-red-400" style={{ color: "var(--muted)" }}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
